import { datadogRum } from '@datadog/browser-rum';
import { isTrackingSuppressed } from './trackingSuppression';

/**
 * Custom event names. Naming: `area.object_action`, snake_case, past-tense.
 * Kept intentionally lean: only events that are actually wired up belong here.
 * Add each new name in the PR that integrates it, so the union never lists an
 * event we don't emit — or one we can't (e.g. login happens on Frontegg's
 * external hosted page, outside our RUM session, so there is no auth.login_* to
 * emit from the front end).
 */
export type DatadogEvent =
  // Navigation
  | 'nav.view_opened'
  // Portfolio custom views (FD-3125)
  | 'portfolio.view_opened'
  | 'portfolio.view_created'
  // Data layer (FD-3117). GraphQL/Apollo was removed from the app (FD-3487), so
  // the data layer is Axios (MaggieHttp) + react-query only.
  | 'http.request'
  | 'http.request_failed'
  | 'query.failed'
  | 'mutation.failed'
  // DealFlow workflow (FD-3119)
  | 'dealflow.deal_tracked'
  | 'dealflow.deal_stage_changed'
  | 'dealflow.analytics_viewed'
  | 'company.profile_viewed'
  | 'company.commentary_added'
  | 'company.field_updated'
  // Finance area (FD-3121)
  | 'finance.statement_viewed'
  | 'finance.period_changed'
  | 'finance.reconciliation_run'
  // Transactions area (FD-3587)
  | 'transactions.viewed'
  | 'transactions.transaction_created'
  | 'transactions.transaction_edited'
  | 'transactions.bulk_transactions_created'
  | 'transactions.bulk_transactions_rejected'
  // Gated module previews
  | 'gate.frosted_viewed'
  | 'grid.exported'
  | 'grid.view_saved'
  | 'grid.filter_applied'
  // Sourcing (FD-3123)
  | 'sourcing.search_run'
  | 'sourcing.bulk_upload_completed'
  // Integrations & Mappings (FD-3124)
  | 'integration.connected'
  | 'mapping.saved'
  // Entity view events (FD-3125)
  | 'portfolio.overview_viewed'
  | 'fund.profile_viewed'
  | 'tearsheet.generated';

type EventAttrs = Record<string, string | number | boolean | undefined>;

// Attrs for `trackTiming`: same as EventAttrs, but `duration_ms` is reserved
// (the function sets it from its own argument). `duration_ms?: never` makes a
// caller-supplied value a compile error rather than a silently-dropped one.
// Note: `Omit<EventAttrs, 'duration_ms'>` would NOT work here — Omit is a no-op
// on an index-signature type, so the collision must be forbidden explicitly.
type TimingAttrs = EventAttrs & { duration_ms?: never };

// Best-effort gate: truthy once `init()` has been called. It is intentionally
// coarse — `datadogRum.addAction` is itself a no-op (buffer) before RUM truly
// starts, so this guard is a cheap fast-path, not a correctness guarantee.
function isRumInitialized(): boolean {
  return Boolean(datadogRum.getInitConfiguration());
}

function canTrack(): boolean {
  return !isTrackingSuppressed() && isRumInitialized();
}

/** Emit a custom RUM action. No-ops safely when RUM is not initialized or tracking is suppressed. */
export function trackEvent(name: DatadogEvent, attrs?: EventAttrs): void {
  if (!canTrack()) {
    return;
  }
  datadogRum.addAction(name, attrs);
}

/**
 * Emit a measured duration as a RUM action carrying `duration_ms`. No-ops when
 * uninitialized. `duration_ms` is reserved and supplied from `durationMs`;
 * `TimingAttrs` forbids it in `attrs` at the type level.
 */
export function trackTiming(name: DatadogEvent, durationMs: number, attrs?: TimingAttrs): void {
  if (!canTrack()) {
    return;
  }
  datadogRum.addAction(name, { ...attrs, duration_ms: durationMs });
}
